'use client';

import type { ToolbarGroupSlotProps } from '@magicschool/spellbook/components/Toolbar/slots';
import {
  ToolbarButtonSlot,
  ToolbarDividerSlot,
  ToolbarGroupSlot,
  ToolbarLinkSlot,
  ToolbarOverflowSlot,
  ToolbarTooltipContentSlot,
  ToolbarTooltipSlot,
} from '@magicschool/spellbook/components/Toolbar/slots';
import type { ToolbarGroupDescriptor } from '@magicschool/spellbook/components/Toolbar/useToolbarOverflow';
import {
  TOOLBAR_GROUP_ID_ATTRIBUTE,
  ToolbarOverflowContext,
  useToolbarOverflow,
} from '@magicschool/spellbook/components/Toolbar/useToolbarOverflow';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { createWrappedContent } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import * as ToolbarPrimitive from 'radix-ui/toolbar';
import { cloneElement, type ReactElement, type ReactNode, type RefCallback, type RefObject } from 'react';

export type ToolbarProps = React.ComponentProps<typeof ToolbarPrimitive.Root> & {
  /** Accessible name for the toolbar, announced alongside its `role="toolbar"`. */
  label: string;
  /** Inserts a divider between neighboring visible groups. Turn off to place `Toolbar.Divider` by hand. */
  withDividers?: boolean;
};

export const toolbarVariants = cva(['flex', 'min-w-0'], {
  variants: {
    /** Row or column. Only a horizontal toolbar collapses groups into the overflow panel. */
    orientation: {
      // `overflow-x-clip`, not `overflow-hidden`: clip is the one value that leaves the cross axis
      // visible, so the overflow indicator can draw outside a row this tight. The padding, cancelled by
      // the negative margin, moves the clip edge out 4px: the reach of `.focus-visible-ring`'s
      // `ring-2 ring-offset-2`, so edge rings stay whole. Change both if that ring changes.
      horizontal: ['flex-row', 'items-center', 'gap-1', 'overflow-x-clip', '-m-1', 'p-1'],
      vertical: ['flex-col', 'items-stretch', 'gap-1'],
    },
  },
});

/** Hands the row to the measurement hook and to the consumer's `ref`, which React 19 passes as a prop. */
function composeRowRef(rowRef: RefObject<HTMLDivElement | null>, consumerRef: ToolbarProps['ref']): RefCallback<HTMLDivElement> {
  return (row) => {
    rowRef.current = row;

    if (typeof consumerRef === 'function') consumerRef(row);
    else if (consumerRef) consumerRef.current = row;
  };
}

/**
 * ArrowDown opens a closed popup from its trigger, as it already does for a menu button. A trigger
 * that handles the key itself prevents its default first, and a horizontal row's arrows never use it.
 */
function openPopupOnArrowDown(event: React.KeyboardEvent<HTMLDivElement>): void {
  if (event.key !== 'ArrowDown' || event.defaultPrevented) return;
  const trigger = event.target;
  if (!(trigger instanceof HTMLElement) || !isClosedPopupTrigger(trigger)) return;

  event.preventDefault();
  trigger.click();
}

function isClosedPopupTrigger(element: HTMLElement): boolean {
  const popup = element.getAttribute('aria-haspopup');
  return popup !== null && popup !== 'false' && element.getAttribute('aria-expanded') === 'false';
}

/** Everything the root needs about one `Toolbar.Group` beyond the element itself. */
function describeGroup(group: ReactElement, index: number): ToolbarGroupDescriptor {
  const props = group.props as Partial<ToolbarGroupSlotProps>;

  return {
    id: String(index),
    collapsible: props.collapsible ?? true,
    collapsePriority: props.collapsePriority ?? index,
    active: props.active ?? false,
  };
}

const ToolbarBase: React.FC<ToolbarProps> = ({
  children,
  className,
  label,
  orientation = 'horizontal',
  withDividers = true,
  ref,
  onKeyDown,
  ...props
}) => {
  const { orderedSlots, rest } = useOrderedSlots(children, {
    groups: { Component: ToolbarGroupSlot },
    buttons: { Component: ToolbarButtonSlot },
    links: { Component: ToolbarLinkSlot },
    tooltips: { Component: ToolbarTooltipSlot },
    dividers: { Component: ToolbarDividerSlot },
    overflow: { Component: ToolbarOverflowSlot },
  });

  const groups = orderedSlots.filter((slot) => slot.key === 'groups').map((slot, index) => describeGroup(slot.component, index));

  const { rowRef, collapsedIds } = useToolbarOverflow({ groups, enabled: orientation === 'horizontal', withDividers });

  const rowItems: ReactNode[] = [];
  const collapsedItems: ReactNode[] = [];
  let groupIndex = -1;
  let hasVisibleGroup = false;

  for (const slot of orderedSlots) {
    const key = `${slot.key}-${slot.originalIndex}`;

    if (slot.key === 'overflow') continue;

    if (slot.key !== 'groups') {
      rowItems.push(cloneElement(slot.component, { key }));
      continue;
    }

    groupIndex += 1;
    const group = slot.component as ReactElement<ToolbarGroupSlotProps>;
    const { id } = describeGroup(group, groupIndex);

    if (collapsedIds.has(id)) {
      if (withDividers && collapsedItems.length > 0) collapsedItems.push(<ToolbarDividerSlot key={`${key}-divider`} />);
      collapsedItems.push(cloneElement(group, { key }));
      continue;
    }

    if (withDividers && hasVisibleGroup) rowItems.push(<ToolbarDividerSlot key={`${key}-divider`} />);
    rowItems.push(cloneElement(group, { key, [TOOLBAR_GROUP_ID_ATTRIBUTE]: id }));
    hasVisibleGroup = true;
  }

  const collapsedGroups = collapsedItems.length > 0 ? collapsedItems : null;
  const hasActiveCollapsed = groups.some((group) => collapsedIds.has(group.id) && group.active);
  const overflow = orderedSlots.find((slot) => slot.key === 'overflow')?.component ?? <ToolbarOverflowSlot />;

  return (
    <ToolbarOverflowContext.Provider value={{ collapsedGroups, hasActiveCollapsed, rowRef }}>
      <ToolbarPrimitive.Root
        ref={composeRowRef(rowRef, ref)}
        orientation={orientation}
        data-spellbook-name="toolbar"
        className={cn(toolbarVariants({ orientation }), className)}
        {...props}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          openPopupOnArrowDown(event);
        }}
        aria-label={label}
      >
        {rowItems}
        {/* Mounted even with nothing collapsed, so it can close a controlled panel as it empties. */}
        {overflow}
        {rest.length > 0 ? (
          <div data-spellbook-name="toolbar-trailing" className="flex shrink-0 items-center gap-1">
            {createWrappedContent(rest)}
          </div>
        ) : null}
      </ToolbarPrimitive.Root>
    </ToolbarOverflowContext.Provider>
  );
};

/**
 * A row of grouped controls that moves the groups it cannot fit into an overflow panel. Built on Radix Toolbar, so the whole row is one tab stop and arrow keys move between controls.
 *
 * ### When to use
 * - For an editor or canvas toolbar: several groups of formatting and insert controls in one row
 * - When the control set is wider than the space it gets, and the extra controls should stay reachable rather than scroll out of view
 * - When a set of controls needs the toolbar keyboard model (one tab stop, arrows between controls) rather than one tab stop per button
 *
 * ### When NOT to use
 * - For one group of related actions that should become a menu when space is tight: use `CollapsibleButtonGroup`, which rebuilds its actions as menu items at a CSS breakpoint
 * - For visually fusing a few buttons together (split buttons, increment/decrement): use `ButtonGroup`
 * - For mutually exclusive choices: use `ToggleGroup` or `Tabs`
 * - For page or app chrome: use `AppBar` or `PageHeader`
 *
 * ### API
 * - `label` (required): Accessible name for the toolbar.
 * - `orientation`: `'horizontal'` (default) or `'vertical'`. A vertical toolbar does not measure and never collapses.
 * - `withDividers`: Inserts a divider between neighboring visible groups. `true` by default; turn it off to place `Toolbar.Divider` by hand.
 * - `loop`, `dir`: Passed to Radix. `loop` (default `true`) wraps arrow-key focus at the ends.
 *
 * ### Composition (slots)
 * - `<Toolbar.Group>`: The unit of collapse. Renders a `role="group"` named by its required `label`.
 *   - `collapsible`: `false` pins the group in the row no matter how narrow the toolbar gets.
 *   - `collapsePriority`: Highest collapses first; equal values collapse right to left. Defaults to the group's position, so the rightmost group goes first.
 *   - `active`: Tells the overflow trigger to show an indicator while this group is hidden. What counts as active is the feature's decision, not the toolbar's.
 * - `<Toolbar.Button>`: Wraps a single `Button`, `IconButton`, or `ToggleIconButton` so it joins the toolbar's focus order.
 * - `<Toolbar.Link>`: Same as `Toolbar.Button`, for a `Link`.
 * - `<Toolbar.Tooltip>`: Wrap around `Toolbar.Button` or `Toolbar.Link` to label an icon-only control. `mode="label"` by default, so the content becomes the control's accessible name.
 * - `<Toolbar.TooltipContent>`: Tooltip text. Renders as `<Text variant="caption-1" />`.
 * - `<Toolbar.Divider>`: A divider between groups. Only needed with `withDividers={false}`; never pass `orientation`.
 * - `<Toolbar.Overflow>`: The overflow trigger and panel. Rendered automatically when something collapses; include it only to configure `label`, `triggerProps`, `tooltip`, `popoverProps`, `contentProps`, or `panelOrientation`. A controlled `popoverProps.open` gets `onOpenChange(false)` when every group fits again, since the panel disappears with its trigger.
 * - Non-slot children: Rendered after the overflow trigger in a trailing region that never collapses (e.g. a save status).
 *
 * ### Wrapping controls
 * A tooltip or a disclosure goes *around* `Toolbar.Button`, never inside it. Radix hands the toolbar's
 * roving-focus props to the outermost element, so a wrapper in between takes them and arrow keys skip
 * the control:
 * ```tsx
 * <Toolbar.Tooltip>
 *   <Toolbar.Button>
 *     <IconButton name="bold" />
 *   </Toolbar.Button>
 *   <Toolbar.TooltipContent>Bold</Toolbar.TooltipContent>
 * </Toolbar.Tooltip>
 *
 * <DropdownMenu>
 *   <DropdownMenu.Trigger>
 *     <Toolbar.Button>
 *       <IconButton name="list" label="List style" />
 *     </Toolbar.Button>
 *   </DropdownMenu.Trigger>
 *   <DropdownMenu.Content>…</DropdownMenu.Content>
 * </DropdownMenu>
 * ```
 *
 * ### Visual behavior
 * - **Overflow**: The row is measured before paint, so groups that do not fit are in the panel by the first frame. A `ResizeObserver` re-runs the decision when the toolbar or a visible group changes width.
 * - **Panel**: Collapsed groups keep their real controls (popovers, menus, pressed toggles) rather than becoming flat menu items. It opens aligned to the trigger's right edge, no wider than the toolbar, and lays the groups out inline, wrapping onto another line when they run out of room; `panelOrientation="vertical"` stacks them in one column instead, moved through with Up and Down, for groups that each hold one labelled option.
 * - **Indicator**: The trigger shows a dot while a hidden group is `active`.
 * - **Ring room**: A horizontal root carries a 4px padding cancelled by a -4px margin so edge focus rings are not clipped. Leave its `p-*` and `m-*` alone; overriding one without the other shifts the controls.
 *
 * ### Accessibility
 * - Radix supplies `role="toolbar"`, `aria-orientation`, and a roving tabindex: one tab stop for the row, arrows between controls, Home/End to the ends.
 * - ArrowDown on a closed popup trigger (`aria-haspopup`, `aria-expanded="false"`) opens it, so a popover opens from the keyboard the way a menu does.
 * - The panel is its own toolbar root named by the overflow `label`, so the collapsed controls keep arrow-key navigation, stay reachable from the keyboard, and the panel's `role="dialog"` is not anonymous.
 * - Each group is a `role="group"` named by its `label`; icon-only controls still need their own name, from `Toolbar.Tooltip` or `IconButton`'s `label`.
 * - `Toolbar.Tooltip` needs a `TooltipProvider` above it, which `ThemeProvider` supplies.
 * - Opening the overflow panel focuses its first control, whose `Toolbar.Tooltip` opens on that focus; the first Escape closes the tooltip and the second closes the panel and returns focus to the trigger.
 * - The indicator dot is visual only, and hidden from assistive technology: which groups are collapsed is not part of the public API, so there is no way to phrase "a hidden control is active" correctly from outside. A feature that needs that announced should track it itself.
 *
 * ### Additional notes
 * - Slots are matched by element type, so compose them directly. A component of your own that returns a `Toolbar.Group` is not recognized as one; extract a function that returns the element instead of a component that renders it.
 * - A group that changes width while it is collapsed keeps its previous measurement until it is back in the row, since a group in the panel is not the row's to measure.
 * - Radix's `Toolbar.ToggleGroup` is not wrapped yet; express toggles as a `Toolbar.Button` around a `ToggleIconButton`.
 */
export const Toolbar = Object.assign(ToolbarBase, {
  Group: ToolbarGroupSlot,
  Button: ToolbarButtonSlot,
  Link: ToolbarLinkSlot,
  Tooltip: ToolbarTooltipSlot,
  TooltipContent: ToolbarTooltipContentSlot,
  Divider: ToolbarDividerSlot,
  Overflow: ToolbarOverflowSlot,
});
